import React from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@material-ui/core';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

const toNumberOrNull = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const computeEsteRScores = (register, scale) => {
    if (!register || !scale) return null;
    const questions = Array.isArray(scale?.questions) ? scale.questions : [];
    const factors = Array.isArray(scale?.factors) ? scale.factors : [];
    const responses = Array.isArray(register?.scaleResponses) ? register.scaleResponses : [];
    if (!questions.length || !responses.length) return null;

    const factorMap = new Map();
    factors.forEach(f => factorMap.set(f.id, f));

    const factorScores = new Map();
    const getFactorEntry = (factorId) => {
        const key = factorId ?? 'none';
        if (!factorScores.has(key)) {
            const factor = factorMap.get(factorId);
            factorScores.set(key, {
                id: factor?.id ?? key,
                code: factor?.code ?? (factorId ? `F${factorId}` : 'F'),
                label: factor?.name ?? 'Otros',
                score: 0,
                maxScore: 0,
            });
        }
        return factorScores.get(key);
    };

    questions.forEach(q => {
        const entry = getFactorEntry(q.scaleFactorId);
        const maxQuestionScore = Math.max(
            ...(q.questionOptionScores || []).map(opt => toNumberOrNull(opt?.score) ?? 0),
            0
        );
        entry.maxScore += Number.isFinite(maxQuestionScore) ? maxQuestionScore : 0;
    });

    let totalScore = 0;
    const questionById = new Map(questions.map(q => [q.id, q]));

    responses.forEach(resp => {
        const q = questionById.get(resp.scaleQuestionId);
        if (!q) return;
        const entry = getFactorEntry(q.scaleFactorId);
        const opt = (q.questionOptionScores || []).find(o => {
            const sid = o.scaleOptionId || o.scaleOption?.id || o.id;
            return Number(sid) === Number(resp.scaleOptionId);
        });
        const score = toNumberOrNull(opt?.score ?? resp.score);
        if (score == null) return;
        totalScore += score;
        entry.score += score;
    });

    const factorsArray = Array.from(factorScores.values());

    const interpretLevel = (label, score) => {
        const name = (label || '').toLowerCase();
        if (!Number.isFinite(score)) return undefined;

        if (name.includes('familiar')) {
            if (score >= 14 && score <= 32) return 'Baja';
            if (score >= 33 && score <= 51) return 'Media';
            return 'Alta';
        }
        if (name.includes('conyugal')) {
            if (score >= 5 && score <= 11) return 'Baja';
            if (score >= 12 && score <= 18) return 'Media';
            return 'Alta';
        }
        if (name.includes('social')) {
            if (score >= 7 && score <= 16) return 'Baja';
            if (score >= 17 && score <= 26) return 'Media';
            return 'Alta';
        }
        if (name.includes('crisis') || name.includes('existencial')) {
            if (score >= 9 && score <= 20) return 'Baja';
            if (score >= 21 && score <= 33) return 'Media';
            return 'Alta';
        }
        return undefined;
    };

    factorsArray.forEach(f => {
        f.level = interpretLevel(f.label || f.name || f.code, f.score);
    });

    return {
        totalScore,
        totalMaxScore: factorsArray.reduce((sum, f) => sum + (f.maxScore || 0), 0),
        factors: factorsArray,
    };
};

export const ScaleResultESTE_R = ({
    title = 'Resultados',
    totalScore,
    factors = [],
    onComputedResult,
    register,
    scale,
}) => {
    const parsedFactors = Array.isArray(factors) ? factors : [];
    const derived = React.useMemo(() => computeEsteRScores(register, scale), [register, scale]);
    React.useEffect(() => {
            if (derived && typeof onComputedResult === 'function') {
                onComputedResult(derived);
            }
        }, [derived, onComputedResult]);

    const fallbackTotalScore = parsedFactors.reduce((sum, factor) => {
        const score = toNumberOrNull(factor?.score) ?? 0;
        return sum + score;
    }, 0);

    const displayTotalScore = toNumberOrNull(totalScore) ?? derived?.totalScore ?? fallbackTotalScore;
    const displayFactors = (derived?.factors && derived.factors.length) ? derived.factors : parsedFactors;

    return (
        <Paper elevation={0} style={{ padding: 24, borderRadius: 12 }}>
            <Typography variant="h5" style={{ fontWeight: 700, marginBottom: 12, color: '#0d1b2a' }}>
                {title}
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16, color: '#0d1b2a' }}>
                Puntuación Total: {displayTotalScore}
            </Typography>

            <List style={{ paddingTop: 0 }}>
                {displayFactors.map((factor, index) => {
                    const score = toNumberOrNull(factor?.score) ?? 0;
                    const label = factor?.label || factor?.name || factor?.title;
                    const level = factor?.level || factor?.severity || factor?.category;
                    const text = label || `Factor ${index + 1}`;

                    return (
                        <ListItem key={factor?.id ?? index} disableGutters>
                            <ListItemIcon style={{ minWidth: 24 }}>
                                <FiberManualRecordIcon fontSize="small" style={{ color: '#0d1b2a' }} />
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ style: { color: '#0d1b2a' } }}
                                primary={<span><strong>{text}:</strong> {score}{level ? ` (${level})` : ''}</span>}
                            />
                        </ListItem>
                    );
                })}
            </List>
            {!displayFactors.length && (
                <Box px={1}>
                    <Typography variant="body2" style={{ color: '#5f6b7a' }}>
                        No hay factores para mostrar.
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default ScaleResultESTE_R;
